// Admin > Compliance: keeping the platform within Indian law day to day — complaints with their
// legal deadlines, identity verification, DPDP data requests, and what is missing.
import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, Stack, Alert,
  Drawer, IconButton, TextField, MenuItem, FormControlLabel, Switch, Divider, Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  COMPLIANCE_OVERVIEW_QUERY, GRIEVANCES_QUERY, UPDATE_GRIEVANCE_MUTATION, PENDING_VERIFICATIONS_QUERY,
  REVIEW_VERIFICATION_MUTATION, DATA_REQUESTS_QUERY, COMPLETE_DATA_REQUEST_MUTATION,
} from '../graphql/queries';
import DocumentReview from '../components/DocumentReview';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const STATUS_COLOR = { open: 'error', acknowledged: 'warning', in_progress: 'info', resolved: 'success', closed: 'default' };

function Tile({ label, value, alert, to, onClick }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', borderColor: alert ? 'error.main' : undefined, cursor: onClick || to ? 'pointer' : 'default' }}
      onClick={onClick} component={to ? RouterLink : 'div'} to={to} style={{ textDecoration: 'none' }}>
      <Typography variant="h5" fontWeight={700} color={alert ? 'error.main' : 'text.primary'}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

function Overview({ o, go }) {
  if (!o) return null;
  return (
    <Box>
      {o.missingLegalDetails.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" component={RouterLink} to="/dashboard/admin/legal">Fix</Button>}>
          Legal documents still show "[to be updated]" for: {o.missingLegalDetails.join(', ')}. The Grievance Officer's name must be published (IT Rules 2021).
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}><Tile label="Complaints open" value={o.grievancesOpen} onClick={() => go(1)} /></Grid>
        <Grid item xs={6} md={3}><Tile label="Not acknowledged in time" value={o.grievancesAckOverdue} alert={o.grievancesAckOverdue > 0} onClick={() => go(1)} /></Grid>
        <Grid item xs={6} md={3}><Tile label="Past resolution deadline" value={o.grievancesResolveOverdue} alert={o.grievancesResolveOverdue > 0} onClick={() => go(1)} /></Grid>
        <Grid item xs={6} md={3}><Tile label="Safety reports open (fraud, dowry, profiles)" value={o.safetyReportsOpen} alert={o.safetyReportsOpen > 0} onClick={() => go(1)} /></Grid>
        <Grid item xs={6} md={3}><Tile label="Verifications waiting" value={o.verificationsPending} alert={o.verificationsPending > 0} onClick={() => go(2)} /></Grid>
        <Grid item xs={6} md={3}><Tile label="Families verified" value={`${o.familiesVerified} / ${o.familiesTotal}`} /></Grid>
        <Grid item xs={6} md={3}><Tile label={`Data requests waiting${o.oldestDataRequestAt ? ` (oldest ${fmt(o.oldestDataRequestAt)})` : ''}`} value={o.dataRequestsPending} alert={o.dataRequestsPending > 0} onClick={() => go(3)} /></Grid>
        <Grid item xs={6} md={3}><Tile label="Users who accepted the current Terms" value={`${o.usersAcceptedTerms} / ${o.activeUsers}`} to="/dashboard/admin/legal" /></Grid>
        <Grid item xs={6} md={3}><Tile label="Live offerings without cancellation terms" value={o.offeringsMissingCancellation} alert={o.offeringsMissingCancellation > 0} /></Grid>
        <Grid item xs={6} md={3}><Tile label="Profiles without the matrimonial declaration" value={o.profilesUndeclared} /></Grid>
      </Grid>
      <Typography variant="caption" color="text.secondary" display="block" mt={2}>
        Users who haven't accepted the current Terms are asked when they next sign in. Vendors must add cancellation terms the next time they edit
        an offering, and profile owners declare when they next save their profile. Every action here is recorded in the <Link component={RouterLink} to="/dashboard/admin/audit-logs">Audit Logs</Link>.
      </Typography>
    </Box>
  );
}

function Grievances({ onChange }) {
  const [status, setStatus] = useState('active');
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState({ status: '', message: '', isPublic: true });
  const { data, refetch } = useQuery(GRIEVANCES_QUERY, { variables: { status: status || null }, fetchPolicy: 'cache-and-network' });
  const [update, { loading, error }] = useMutation(UPDATE_GRIEVANCE_MUTATION, {
    onCompleted: (d) => { setOpen(d.updateGrievance); setReply({ status: '', message: '', isPublic: true }); refetch(); onChange(); },
  });
  const rows = data?.grievances || [];
  return (
    <Box>
      <TextField select size="small" label="Show" value={status} onChange={e => setStatus(e.target.value)} sx={{ mb: 2, minWidth: 200 }}>
        <MenuItem value="active">Open (not resolved)</MenuItem>
        <MenuItem value="">All</MenuItem>
        {Object.keys(STATUS_COLOR).map(s => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
      </TextField>
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Ticket</TableCell><TableCell>From</TableCell><TableCell>About</TableCell><TableCell>Status</TableCell><TableCell>Due</TableCell></TableRow></TableHead>
          <TableBody>
            {rows.map(g => (
              <TableRow key={g.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(g)}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{g.ticketNumber}<Typography variant="caption" display="block" color="text.secondary">{fmt(g.createdAt)}</Typography></TableCell>
                <TableCell>{g.name}<Typography variant="caption" display="block" color="text.secondary">{g.email}</Typography></TableCell>
                <TableCell><Chip size="small" variant="outlined" label={g.category.replace('_', ' ')} sx={{ mr: 0.5 }} />{g.subject}</TableCell>
                <TableCell><Chip size="small" color={STATUS_COLOR[g.status]} label={g.status.replace('_', ' ')} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {g.overdue && <Chip size="small" color="error" label="Overdue" sx={{ mr: 0.5 }} />}
                  {!g.acknowledgedAt ? `Ack by ${fmt(g.ackDueAt)}` : !['resolved', 'closed'].includes(g.status) ? `Resolve by ${fmt(g.resolveDueAt)}` : `Resolved ${fmt(g.resolvedAt)}`}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No complaints.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Drawer anchor="right" open={Boolean(open)} onClose={() => setOpen(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
        {open && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700}>{open.ticketNumber}</Typography>
              <IconButton onClick={() => setOpen(null)}><CloseIcon /></IconButton>
            </Stack>
            <Typography fontWeight={600} mt={1}>{open.subject}</Typography>
            <Typography variant="body2" color="text.secondary">{open.name} · {open.email}{open.phone ? ` · ${open.phone}` : ''}</Typography>
            {open.relatedType === 'user' && <Typography variant="caption" display="block">Reported member: {open.relatedId} (see Admin → Users)</Typography>}
            <Paper variant="outlined" sx={{ p: 1.5, my: 1.5, whiteSpace: 'pre-line' }}><Typography variant="body2">{open.description}</Typography></Paper>
            <Typography variant="caption" color="text.secondary" display="block">
              Acknowledge by {fmt(open.ackDueAt)}{open.acknowledgedAt ? ` (done ${fmt(open.acknowledgedAt)})` : ''} · resolve by {fmt(open.resolveDueAt)}
            </Typography>
            <Divider sx={{ my: 2 }} />
            {open.timeline.map((e, i) => (
              <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                <b>{fmt(e.at)}</b> {e.status?.replace('_', ' ')}{e.message ? ` — ${e.message}` : ''}{!e.public && <Chip size="small" label="internal" sx={{ ml: 0.5, height: 18 }} />}
              </Typography>
            ))}
            {!['closed'].includes(open.status) && (
              <Stack spacing={1.5} mt={2}>
                {error && <Alert severity="error">{error.message}</Alert>}
                <TextField select size="small" label="Change status" value={reply.status} onChange={e => setReply(r => ({ ...r, status: e.target.value }))}>
                  <MenuItem value="">(keep {open.status.replace('_', ' ')})</MenuItem>
                  {['acknowledged', 'in_progress', 'resolved', 'closed'].map(s => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
                </TextField>
                <TextField size="small" label={reply.isPublic ? 'Message to the complainant (emailed)' : 'Internal note'} multiline minRows={3}
                  value={reply.message} onChange={e => setReply(r => ({ ...r, message: e.target.value }))} />
                <FormControlLabel control={<Switch checked={reply.isPublic} onChange={e => setReply(r => ({ ...r, isPublic: e.target.checked }))} />} label="Send to the complainant" />
                <Button variant="contained" disabled={loading || (!reply.status && !reply.message.trim())}
                  onClick={() => update({ variables: { id: open.id, status: reply.status || null, message: reply.message || null, isPublic: reply.isPublic } })}>
                  Save
                </Button>
              </Stack>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

function Verifications({ onChange }) {
  const { data, refetch } = useQuery(PENDING_VERIFICATIONS_QUERY, { fetchPolicy: 'network-only' });
  const [notes, setNotes] = useState({});
  const [show, setShow] = useState('all'); // all | vendor | family
  const [review, { error }] = useMutation(REVIEW_VERIFICATION_MUTATION, { onCompleted: () => { refetch(); onChange(); } });
  const all = data?.pendingVerifications || [];
  const rows = all.filter(v => show === 'all' || (show === 'vendor' ? v.roleName === 'vendor' : v.roleName !== 'vendor'));
  const changed = () => { refetch(); onChange(); };
  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      <Alert severity="info" sx={{ mb: 2 }}>
        Approve or reject each document. Families: check that the name, date of birth and gender match the profile, and the legal age (bride 18, groom 21).
        Vendors: once the owner identity proof and a business document are approved, the vendor can add their services.
      </Alert>
      <Stack direction="row" spacing={1} mb={2}>
        {[['all', 'All'], ['vendor', 'Vendors'], ['family', 'Families & others']].map(([k, l]) => (
          <Chip key={k} label={`${l} (${all.filter(v => k === 'all' || (k === 'vendor' ? v.roleName === 'vendor' : v.roleName !== 'vendor')).length})`}
            color={show === k ? 'primary' : 'default'} variant={show === k ? 'filled' : 'outlined'} onClick={() => setShow(k)} />
        ))}
      </Stack>
      {!rows.length && <Typography color="text.secondary">No verifications waiting.</Typography>}
      <Stack spacing={2}>
        {rows.map(v => (
          <Paper key={v.userId} variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight={700}>{v.businessName || v.name} <Typography component="span" variant="body2" color="text.secondary">· {v.email} · {v.role}</Typography></Typography>
            {v.roleName !== 'vendor' && <Typography variant="body2" color="text.secondary" mb={1}>Profile: {v.gender || '—'}, born {v.dob || '—'}</Typography>}
            <Box mt={1}><DocumentReview v={v} onChanged={changed} /></Box>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Or decide for the whole account (every document waiting):</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField size="small" fullWidth placeholder="Note (required to reject — the user sees it)" value={notes[v.userId] || ''} onChange={e => setNotes(n => ({ ...n, [v.userId]: e.target.value }))} />
              <Button variant="outlined" color="success" onClick={() => review({ variables: { userId: v.userId, decision: 'verified', note: notes[v.userId] || null } })}>Verify all</Button>
              <Button variant="outlined" color="error" onClick={() => review({ variables: { userId: v.userId, decision: 'rejected', note: notes[v.userId] || null } })}>Reject all</Button>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}

function DataRequests({ onChange }) {
  const { data, refetch } = useQuery(DATA_REQUESTS_QUERY, { fetchPolicy: 'network-only' });
  const [notes, setNotes] = useState({});
  const [complete, { error }] = useMutation(COMPLETE_DATA_REQUEST_MUTATION, { onCompleted: () => { refetch(); onChange(); } });
  const rows = data?.dataRequests || [];
  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      <Alert severity="warning" sx={{ mb: 2 }}>
        Completing an <b>erasure</b> request closes the account and erases the profile, photos, contacts, guests, invitations and identity documents
        at once. Contracts, bookings, payment records and the audit trail are kept as the law requires. This cannot be undone.
      </Alert>
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Requested</TableCell><TableCell>User</TableCell><TableCell>Request</TableCell><TableCell>Status</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(r.createdAt)}</TableCell>
                <TableCell>{r.email}</TableCell>
                <TableCell>{{ erasure: 'Close account & erase data', correction: 'Correction' }[r.type] || r.type}{r.details && <Typography variant="caption" display="block" color="text.secondary">{r.details}</Typography>}</TableCell>
                <TableCell><Chip size="small" label={r.status} color={r.status === 'pending' ? 'warning' : r.status === 'completed' ? 'success' : 'default'} />{r.note && <Typography variant="caption" display="block">{r.note}</Typography>}</TableCell>
                <TableCell sx={{ minWidth: 300 }}>
                  {r.status === 'pending' && (
                    <Stack direction="row" spacing={1}>
                      <TextField size="small" placeholder="Note / reason" value={notes[r.id] || ''} onChange={e => setNotes(n => ({ ...n, [r.id]: e.target.value }))} />
                      <Button size="small" variant="contained" color={r.type === 'erasure' ? 'error' : 'primary'} onClick={() => complete({ variables: { id: r.id, action: 'complete', note: notes[r.id] || null } })}>
                        {r.type === 'erasure' ? 'Erase' : 'Done'}
                      </Button>
                      <Button size="small" onClick={() => complete({ variables: { id: r.id, action: 'reject', note: notes[r.id] || null } })}>Reject</Button>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No data requests.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

export default function AdminCompliancePage() {
  const [tab, setTab] = useState(0);
  const { data, refetch } = useQuery(COMPLIANCE_OVERVIEW_QUERY, { fetchPolicy: 'network-only' });
  const o = data?.complianceOverview;
  return (
    <Box>
      <Typography variant="h4" fontWeight={700}>Compliance</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Complaints with their legal deadlines (IT Rules 2021, E-Commerce Rules 2020), identity verification (MeitY advisory for matrimonial sites),
        and personal data requests (DPDP Act 2023).
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Overview" />
        <Tab label={`Complaints${o?.grievancesOpen ? ` (${o.grievancesOpen})` : ''}`} />
        <Tab label={`Verifications${o?.verificationsPending ? ` (${o.verificationsPending})` : ''}`} />
        <Tab label={`Data requests${o?.dataRequestsPending ? ` (${o.dataRequestsPending})` : ''}`} />
      </Tabs>
      {tab === 0 && <Overview o={o} go={setTab} />}
      {tab === 1 && <Grievances onChange={refetch} />}
      {tab === 2 && <Verifications onChange={refetch} />}
      {tab === 3 && <DataRequests onChange={refetch} />}
    </Box>
  );
}
